import React, { useRef, useEffect, useMemo } from "react";
import { Category, CategoryData } from "data/data";

interface TabCategoriesFilterProps {
  categoryData?: CategoryData[] | Category[];
  activeCategoryId?: number | null;
  onCategoryClick?: (categoryId: number | null) => void;
  // Legacy props (kept for compatibility but not used in new flow)
  setItemData?: any;
  renderAllData?: any;
  searchProduct?: any;
}

const TabCategoriesFilter: React.FC<TabCategoriesFilterProps> = ({
  categoryData,
  activeCategoryId = null,
  onCategoryClick,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const categoryItems = useMemo(() => {
    if (!categoryData || categoryData.length === 0) return [];
    return (categoryData as CategoryData[])
      .filter((cat) => cat.items?.length > 0);
  }, [categoryData]);

  // Scroll active tab into view when it changes
  useEffect(() => {
    if (!scrollRef.current || activeCategoryId === null) return;
    const id = activeCategoryId === -1 ? "all" : String(activeCategoryId);
    const activeTab = scrollRef.current.querySelector(`[data-tab-id="${id}"]`);
    if (activeTab) {
      activeTab.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [activeCategoryId]);

  return (
    <div className="sticky top-0 z-30 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 shadow-sm dark:shadow-slate-800/30">
      <div
        ref={scrollRef}
        className="flex gap-1 px-3 py-2 overflow-x-auto scrollbar-hide"
        style={{
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {/* "All" tab */}
        <button
          data-tab-id="all"
          onClick={() => onCategoryClick?.(null)}
          className={`flex-shrink-0 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
            activeCategoryId === null
              ? "bg-primary-500 dark:bg-primary-400 text-white dark:text-slate-900 shadow-sm"
              : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-slate-200"
          }`}
        >
          All
        </button>

        {categoryItems.map((category) => {
          const isActive = activeCategoryId === category.id;
          return (
            <button
              key={category.id}
              data-tab-id={category.id}
              onClick={() => onCategoryClick?.(category.id)}
              className={`flex-shrink-0 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                isActive
                  ? "bg-primary-500 dark:bg-primary-400 text-white dark:text-slate-900 shadow-sm"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-slate-200"
              }`}
            >
              {category.name}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TabCategoriesFilter;
